import { isRoundedPlatform, roundedTopClassname } from '@/helpers/environmentHelper';
import { selectXplorSpotMobileLayoutNode, setXplorSpotMobileLayoutNode } from '@/state/reducers/xplorSpotLayoutSlice';
import useLanguage from '@/utils/hooks/useLanguage';
import { DEFAULT_MOBILE_LAYOUT, XplorSpotModules } from '@/utils/hooks/useXplorSpotLayout';
import cn from 'classnames';
import { useMemo } from 'react';
import { IconType } from 'react-icons/lib';
import {
    MdAccountBalanceWallet,
    MdCandlestickChart,
    MdHistory,
    MdPriceChange,
    MdSwapVerticalCircle
} from 'react-icons/md';
import { updateTree } from 'react-mosaic-component';
import { useAppDispatch, useAppSelector } from '../../state/hooks';

export const mobileTabNavigationClassName = cn(
    'w-full h-full grid grid-flow-col auto-cols-fr divide-x-0 2xs:divide-x bg-brand-background-dark overflow-clip border-t border-brand-neutral-700',
    roundedTopClassname,
    { '2xs:divide-brand-accent': isRoundedPlatform, '2xs:divide-brand-neutral-700': !isRoundedPlatform }
);

const tabs: {
    label: string;
    name: XplorSpotModules;
    Icon: IconType;
}[] = [
    { label: 'Order', name: 'Order', Icon: MdSwapVerticalCircle },
    { label: 'Depth', name: 'Order Book', Icon: MdPriceChange },
    { label: 'Chart', name: 'Chart', Icon: MdCandlestickChart },
    { label: 'Blotters', name: 'Blotter', Icon: MdHistory },
    { label: 'Balances', name: 'Balances', Icon: MdAccountBalanceWallet }
];

const MobileTabNavigation = () => {
    const dispatch = useAppDispatch();
    const mobileLayoutNode = useAppSelector(selectXplorSpotMobileLayoutNode) as any;
    const layout = useMemo(() => (mobileLayoutNode ? mobileLayoutNode : DEFAULT_MOBILE_LAYOUT), [mobileLayoutNode]);
    const lang = useLanguage();

    const tabs: {
        label: string;
        name: XplorSpotModules;
        title: string;
        Icon: IconType;
    }[] = useMemo(() => {
        return [
            {
                label: lang.locale === 'en' ? 'Order' : lang.modules.order,
                name: 'Order',
                title: 'Order',
                Icon: MdSwapVerticalCircle
            },
            {
                label: lang.locale === 'en' ? 'Depth' : lang.modules['order-book'],
                name: 'Order Book',
                title: 'Market Depth',
                Icon: MdPriceChange
            },
            {
                label: lang.locale === 'en' ? 'Chart' : lang.modules.chart,
                name: 'Chart',
                title: 'Chart',
                Icon: MdCandlestickChart
            },
            {
                label: lang.locale === 'en' ? 'Blotters' : lang.modules.blotter,
                name: 'Blotter',
                title: 'Blotter',
                Icon: MdHistory
            },
            {
                label: lang.locale === 'en' ? 'Balances' : lang.modules.balances,
                name: 'Balances',
                title: 'Balances',
                Icon: MdAccountBalanceWallet
            }
        ];
    }, [lang]);

    return (
        <div className={mobileTabNavigationClassName}>
            {tabs.map((tab, index) => (
                <TabButton
                    key={tab.name}
                    Icon={tab.Icon}
                    isActive={layout?.first === tab.name}
                    onClick={() => switchTab(layout, tab.name, dispatch)}>
                    {tab.label}
                </TabButton>
            ))}
        </div>
    );
};

export default MobileTabNavigation;

export const TabButton = ({ Icon, isActive, onClick, children }: any) => {
    return (
        <button
            className={cn(
                'h-full flex flex-col items-center justify-center gap-0.5 p-2 min-w-[42px]',
                isRoundedPlatform
                    ? {
                          ['text-brand-module-title bg-brand-primary-dark']: isActive,
                          ['text-brand-module-title bg-brand-accent hover:bg-brand-accent-light']: !isActive
                      }
                    : {
                          ['text-brand-primary']: isActive,
                          ['']: !isActive
                      }
            )}
            onClick={onClick}>
            <div>
                <Icon className="w-4 h-4" />
            </div>
            <span className="text-2xs 2xs:text-xs">{children}</span>
        </button>
    );
};

const switchTab = (mobileLayoutNode, moduleToDrop, dispatch) => {
    let currentNode = mobileLayoutNode;
    if (currentNode) {
        currentNode = updateTree(currentNode, [
            {
                path: [],
                spec: {
                    $set: {
                        direction: 'row',
                        first: moduleToDrop,
                        second: '',
                        splitPercentage: 100
                    }
                }
            }
        ]);
    } else {
        currentNode = moduleToDrop;
    }
    dispatch(setXplorSpotMobileLayoutNode(currentNode));
};
